/**
 * ToolCallGroup — groups consecutive tool calls into a collapsible block.
 *
 * Shows running tool calls expanded, with completed ones folded into a compact
 * summary line. Clicking the summary expands all.
 *
 * When a running tool finishes it leaves the visible set and folds into the
 * summary. Rather than unmounting it in a single frame — which drops the row
 * height abruptly and jerks the message list — the leaving block is kept mounted
 * for one `collapseAway` animation so its height shrinks to 0 smoothly.
 */

import { useState, useEffect, useRef } from "react";
import type { ToolCallState } from "@/api/types";
import { ToolCallBlock } from "./ToolCallBlock";
import { toolMeta } from "@/theme";

interface Props {
  toolCalls: ToolCallState[];
}

// Kept in sync with the `collapseAway` keyframe duration in index.css; the
// snapshot is unmounted a hair after the animation ends.
const COLLAPSE_MS = 220;

export function ToolCallGroup({ toolCalls }: Props) {
  const [expanded, setExpanded] = useState(false);

  const running = toolCalls.filter((tc) => tc.status === "running");
  const completed = toolCalls.filter((tc) => tc.status !== "running");
  const hasRunning = running.length > 0;

  // When collapsed: show running tools as blocks + the rest in the summary.
  // When expanded: show every tool call, with a Collapse control below.
  const visibleItems = expanded ? toolCalls : hasRunning ? running : [];

  // Blocks that just left the visible set, kept mounted so they can collapse
  // away instead of vanishing. Snapshots (not live refs) so a finished tool
  // renders in its completed state while it folds into the summary.
  const [exiting, setExiting] = useState<ToolCallState[]>([]);
  const prevVisibleRef = useRef<ToolCallState[]>(visibleItems);

  const visibleKey = visibleItems.map((tc) => tc.tool_use_id).join(",");

  useEffect(() => {
    const cur = new Set(visibleItems.map((tc) => tc.tool_use_id));
    const gone = prevVisibleRef.current.filter((tc) => !cur.has(tc.tool_use_id));
    prevVisibleRef.current = visibleItems;
    if (expanded || gone.length === 0) return;

    // Snapshot each leaving block in its latest (finished) state.
    const goneFinished = gone.map(
      (tc) => toolCalls.find((t) => t.tool_use_id === tc.tool_use_id) ?? tc,
    );
    setExiting((prev) => {
      const ids = new Set(prev.map((p) => p.tool_use_id));
      return [...prev, ...goneFinished.filter((g) => !ids.has(g.tool_use_id))];
    });
    const goneIds = new Set(gone.map((tc) => tc.tool_use_id));
    const timer = window.setTimeout(() => {
      setExiting((prev) => prev.filter((p) => !goneIds.has(p.tool_use_id)));
    }, COLLAPSE_MS + 40);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, expanded]);

  // Single tool — no grouping needed. (Hooks above still run so the rules of
  // hooks hold; their exit-animation state is simply unused for one block.)
  if (toolCalls.length === 1) {
    return <ToolCallBlock toolCall={toolCalls[0]!} />;
  }

  // ``completed`` is the set folded into the summary accordion. Its size
  // is what gates the Collapse control, so it must be derived independent
  // of ``expanded`` — otherwise expanding zeroes the count and the
  // Collapse button (gated on ``collapsedCount > 0``) can never render,
  // trapping the user in the expanded view.
  const collapsibleItems = completed;
  const collapsedCount = collapsibleItems.length;

  // Build summary text from the collapsed (completed) items
  const summaryParts: string[] = [];
  const nameCounts = new Map<string, number>();
  for (const tc of collapsibleItems) {
    const mcpMatch = tc.tool_name.match(/^mcp__\w+__(.+)$/);
    const rawName = mcpMatch?.[1] ?? tc.tool_name;
    const meta = toolMeta[rawName] ?? toolMeta[tc.tool_name];
    const label = meta?.label ?? rawName;
    nameCounts.set(label, (nameCounts.get(label) ?? 0) + 1);
  }
  for (const [label, count] of nameCounts) {
    summaryParts.push(count > 1 ? `${label} (x${count})` : label);
  }

  const errorCount = collapsibleItems.filter((tc) => tc.status === "error").length;

  // Never show a block twice (a leaving snapshot that somehow re-enters the
  // visible set would collide on key), and render leaving blocks after the
  // live ones so they collapse in place.
  const visibleIds = new Set(visibleItems.map((tc) => tc.tool_use_id));
  const exitingOnly = exiting.filter((tc) => !visibleIds.has(tc.tool_use_id));
  const exitingIds = new Set(exitingOnly.map((tc) => tc.tool_use_id));

  return (
    <div className="space-y-1.5">
      {/* Collapsed summary — only while collapsed. Matches the quiet tool-row
          chrome: left chevron, thin border, muted text. A failure count is the
          only colored cue, and only when something actually failed. */}
      {!expanded && collapsedCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="group w-full flex items-center gap-1.5 px-3 py-2 rounded-lg border border-space-20
            bg-space-10/60 hover:bg-space-20/60 text-xs font-medium text-space-50 hover:text-space
            transition-colors text-left overflow-hidden"
        >
          {/* Chevron — same expand affordance as a single tool row / the thoughts panel */}
          <svg
            className="w-3 h-3 shrink-0 text-space-40 group-hover:text-space transition-colors"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>

          <span className="shrink-0">
            {collapsedCount} tool{collapsedCount !== 1 ? "s" : ""}
          </span>
          <span className="text-space-40 font-normal truncate">
            {summaryParts.join(", ")}
          </span>

          {errorCount > 0 && (
            <span className="ml-auto shrink-0 text-hydra font-normal">
              {errorCount} failed
            </span>
          )}
        </button>
      )}

      {/* Visible tool blocks — every call when expanded, otherwise just the
          running ones (completed are folded into the summary above), plus any
          just-finished blocks collapsing away. Each block is wrapped in a
          stable-keyed div so a block transitioning from visible → collapsing
          reuses its DOM node (no remount, no re-triggered enter animation).
          The expand animation wraps the set so newly-revealed blocks slide in
          together. */}
      <div className={expanded ? "space-y-1.5 animate-[slideDown_200ms_ease-out]" : "space-y-1.5"}>
        {[...visibleItems, ...exitingOnly].map((tc) => (
          <div
            key={tc.tool_use_id}
            className={exitingIds.has(tc.tool_use_id)
              ? "overflow-hidden animate-[collapseAway_220ms_ease-out_forwards]"
              : undefined}
          >
            <ToolCallBlock toolCall={tc} />
          </div>
        ))}
      </div>

      {/* Collapse button when expanded */}
      {expanded && collapsedCount > 0 && (
        <button
          onClick={() => setExpanded(false)}
          className="w-full text-center py-1 text-xs text-space-40 hover:text-space transition-colors"
        >
          Collapse
        </button>
      )}
    </div>
  );
}
